import { useState, useEffect, useRef } from 'react';

export interface EnabledFeatures {
  keywordAssist: boolean;
  contentStrategy: boolean;
  blogPostIdeas: boolean;
  localSeo: boolean;
  reviewAutomation: boolean;
}

interface UseOrbitFeaturesOptions {
  orbitAiApiData: any;
  localSeoApiData: any;
  reviewAutomationSettingsData: any;
  userAgentDetails: any;
  isLocalSeoConfiguredToOtherSite?: boolean;
  isReviewAutomationConfiguredToOtherSite?: boolean;
}

/**
 * Manages the `enabledFeatures` toggle state and one-time initialisation
 * from API data when `userAgentDetails` first loads.
 */
export const useOrbitFeatures = ({
  orbitAiApiData,
  localSeoApiData,
  reviewAutomationSettingsData,
  userAgentDetails,
  isLocalSeoConfiguredToOtherSite = false,
  isReviewAutomationConfiguredToOtherSite = false
}: UseOrbitFeaturesOptions) => {
  const [enabledFeatures, setEnabledFeatures] = useState<EnabledFeatures>({
    keywordAssist: false,
    contentStrategy: false,
    blogPostIdeas: false,
    localSeo: false,
    reviewAutomation: false
  });

  const hasInitializedFeaturesRef = useRef(false);

  useEffect(() => {
    if (userAgentDetails?.response && !hasInitializedFeaturesRef.current) {
      hasInitializedFeaturesRef.current = true;

      const isLocalSeoEnabled = (Array.isArray(localSeoApiData)
        ? localSeoApiData.some((item) => item?.enabled)
        : (localSeoApiData as any)?.enabled ?? false) && !isLocalSeoConfiguredToOtherSite;

      const isReviewAutomationEnabled =
        (reviewAutomationSettingsData?.settings?.enabled ??
        userAgentDetails?.response?.review_automation?.enabled ??
        false) && !isReviewAutomationConfiguredToOtherSite;

      setEnabledFeatures({
        keywordAssist: orbitAiApiData?.keyword_assist?.enabled ?? false,
        contentStrategy: orbitAiApiData?.content_strategy?.enabled ?? false,
        blogPostIdeas: orbitAiApiData?.blog_generation?.enabled ?? false,
        localSeo: isLocalSeoEnabled,
        reviewAutomation: isReviewAutomationEnabled
      });
    }
  }, [userAgentDetails, orbitAiApiData, localSeoApiData, reviewAutomationSettingsData, isLocalSeoConfiguredToOtherSite, isReviewAutomationConfiguredToOtherSite]);

  const toggleFeature = (feature: keyof EnabledFeatures) => {
    setEnabledFeatures((prev) => ({
      ...prev,
      [feature]: !prev[feature]
    }));
  };

  const isAnyFeatureEnabled = Object.values(enabledFeatures).some(
    (val) => val === true
  );

  return {
    enabledFeatures,
    toggleFeature,
    isAnyFeatureEnabled
  };
};
